import React from 'react';
import { AlertTriangle, FileSpreadsheet, Receipt } from 'lucide-react';

const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fr = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '');

export const ApercuImport = ({ c, id, exclus, setExclus, modifiable }) => {
  const lignes = c.lignes || [];
  const champs = Object.keys(c.libelles || {});
  const selectionnables = lignes.filter((l) => !l.erreur);
  const nb = selectionnables.filter((l) => !exclus.includes(l.idx)).length;
  const basculer = (idx) => setExclus((x) => (x.includes(idx) ? x.filter((v) => v !== idx) : [...x, idx]));
  const tout = (on) => setExclus(on ? lignes.filter((l) => l.erreur).map((l) => l.idx) : lignes.map((l) => l.idx));
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-700" data-testid={`klarys-import-${id}`}>
      <p className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-800">
        <FileSpreadsheet className="h-3.5 w-3.5 text-[#0a3d62]" />
        <span data-testid={`klarys-import-compteur-${id}`}>{nb} / {lignes.length} ligne(s) à créer</span>
        <span className="font-normal text-slate-500">— {c.fichier_nom} · aucune invitation envoyée</span>
        {modifiable && (
          <span className="ml-auto flex gap-2 font-normal">
            <button type="button" onClick={() => tout(true)} className="text-[#0a3d62] underline" data-testid={`klarys-import-tout-${id}`}>Tout cocher</button>
            <button type="button" onClick={() => tout(false)} className="text-slate-500 underline" data-testid={`klarys-import-aucun-${id}`}>Tout décocher</button>
          </span>
        )}
      </p>
      <p className="text-[11px] text-slate-500">Colonnes reconnues : {champs.map((k) => `${c.libelles[k]} ← « ${c.colonnes[k]} »`).join(' · ')}</p>
      <div className="max-h-72 overflow-auto rounded border border-slate-100">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
            <tr><th className="px-1.5 py-1" /><th className="px-1.5 py-1">L.</th>{champs.map((k) => <th key={k} className="px-1.5 py-1">{c.libelles[k]}</th>)}<th className="px-1.5 py-1">Contrôle</th></tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => {
              const coche = !l.erreur && !exclus.includes(l.idx);
              return (
                <tr key={l.idx} className={`border-t border-slate-100 ${coche ? '' : 'opacity-50'}`} data-testid={`klarys-import-ligne-${id}-${i}`}>
                  <td className="px-1.5 py-1"><input type="checkbox" checked={coche} disabled={!modifiable || !!l.erreur} onChange={() => basculer(l.idx)}
                    className="h-3.5 w-3.5 accent-[#0a3d62]" data-testid={`klarys-import-check-${id}-${i}`} /></td>
                  <td className="px-1.5 py-1 text-slate-400">{l.ligne}</td>
                  {champs.map((k) => <td key={k} className="max-w-[160px] truncate px-1.5 py-1">{l.donnees[k] || ''}</td>)}
                  <td className="px-1.5 py-1" data-testid={`klarys-import-controle-${id}-${i}`}>
                    {l.erreur && <span className="rounded bg-rose-100 px-1 font-semibold text-rose-700">{l.erreur}</span>}
                    {l.doublon && <span className="rounded bg-amber-100 px-1 font-semibold text-amber-800">Doublon : {l.doublon}</span>}
                    {!l.erreur && !l.doublon && (l.avertissements?.length ? <span className="text-amber-700">{l.avertissements.join(', ')}</span> : <span className="text-emerald-700">OK</span>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ApercuRapprochement = ({ c, id, choix, setChoix, mode, setMode, modifiable }) => {
  const cands = c.candidats || [];
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-700" data-testid={`klarys-rapprochement-${id}`}>
      <p className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-800">
        <Receipt className="h-3.5 w-3.5 text-[#0a3d62]" /> Reçu lu : <span data-testid={`klarys-rapprochement-montant-${id}`}>{eur(c.montant)}</span> le {fr(c.date_paiement)}
        {c.reference_paiement && <span className="font-normal text-slate-500">— réf. {c.reference_paiement}</span>}
        <label className="ml-auto inline-flex items-center gap-1 font-normal">Mode :
          <select value={mode} disabled={!modifiable} onChange={(e) => setMode(e.target.value)} className="rounded border border-slate-300 px-1 py-0.5" data-testid={`klarys-rapprochement-mode-${id}`}>
            <option value="virement">Virement</option><option value="cheque">Chèque</option>
          </select>
        </label>
      </p>
      <p className="text-[11px] text-slate-500">Contrat à régulariser{cands.length > 1 ? ' (choisissez le bon)' : ''} — reçu « {c.fichier_nom} » classé dans la GED du membre :</p>
      <div className="space-y-1.5">
        {cands.map((k, i) => (
          <label key={`${k.produit}-${k.ident}`} className={`block cursor-pointer rounded border px-2 py-1.5 ${choix === i ? 'border-[#0a3d62] bg-sky-50' : 'border-slate-200'}`} data-testid={`klarys-rapprochement-cand-${id}-${i}`}>
            <span className="flex items-center gap-2">
              <input type="radio" name={`rappro-${id}`} checked={choix === i} disabled={!modifiable} onChange={() => setChoix(i)} className="accent-[#0a3d62]" data-testid={`klarys-rapprochement-radio-${id}-${i}`} />
              <b className="text-slate-800">{k.nom}</b> · {k.produit_label} {k.reference}
              <span className="rounded bg-rose-100 px-1 text-[10px] font-semibold text-rose-700">{k.etat_label}</span>
              <span className="ml-auto text-slate-500">dû : {eur(k.total_a_regler)}</span>
            </span>
            <span className="mt-1 block pl-5 text-[11px] text-slate-600">
              Échéances marquées payées : {k.echeances.map((e) => `${fr(e.date)} (${eur(e.montant)})`).join(', ')}
              {k.reactivation && <> · <b>réactivation</b> (contribution suspensive {eur(k.suspensive)})</>}
            </span>
            {Math.abs(k.ecart) >= 0.01 && (
              <span className="mt-0.5 flex items-center gap-1 pl-5 text-[11px] font-semibold text-amber-700" data-testid={`klarys-rapprochement-ecart-${id}-${i}`}>
                <AlertTriangle className="h-3 w-3" /> Écart de {eur(k.ecart)} entre le reçu et les échéances couvertes
              </span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
};

const CONFIANCE = { sur: ['Sûr', 'bg-emerald-100 text-emerald-800'], a_verifier: ['À vérifier', 'bg-amber-100 text-amber-800'], non_trouve: ['Non trouvé', 'bg-slate-100 text-slate-600'] };

export const ApercuReleve = ({ c, id, exclus, setExclus, choix, setChoix, modifiable }) => {
  const lignes = c.lignes || [];
  const coche = (l) => l.candidats?.length > 0 && !exclus.includes(l.idx);
  const nb = lignes.filter(coche);
  const total = nb.reduce((t, l) => t + l.montant, 0);
  const basculer = (idx) => setExclus((x) => (x.includes(idx) ? x.filter((v) => v !== idx) : [...x, idx]));
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-700" data-testid={`klarys-releve-${id}`}>
      <p className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-800">
        <Receipt className="h-3.5 w-3.5 text-[#0a3d62]" />
        <span data-testid={`klarys-releve-compteur-${id}`}>{nb.length} / {lignes.length} virement(s) à enregistrer — {eur(total)}</span>
        <span className="font-normal text-slate-500">— {c.fichier_nom} · mode virement · relevé classé dans la GED des membres rapprochés</span>
      </p>
      <div className="max-h-80 overflow-auto rounded border border-slate-100">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
            <tr><th className="px-1.5 py-1" /><th className="px-1.5 py-1">Date</th><th className="px-1.5 py-1">Montant</th><th className="px-1.5 py-1">Libellé / payeur</th><th className="px-1.5 py-1">Contrat proposé</th><th className="px-1.5 py-1">Confiance</th></tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => {
              const [lab, cls] = CONFIANCE[l.confiance] || CONFIANCE.non_trouve;
              const k = choix[l.idx] ?? l.choix ?? 0;
              const cand = l.candidats?.[k];
              return (
                <tr key={l.idx} className={`border-t border-slate-100 align-top ${coche(l) ? '' : 'opacity-60'}`} data-testid={`klarys-releve-ligne-${id}-${i}`}>
                  <td className="px-1.5 py-1"><input type="checkbox" checked={coche(l)} disabled={!modifiable || !l.candidats?.length} onChange={() => basculer(l.idx)}
                    className="h-3.5 w-3.5 accent-[#0a3d62]" data-testid={`klarys-releve-check-${id}-${i}`} /></td>
                  <td className="whitespace-nowrap px-1.5 py-1">{fr(l.date)}</td>
                  <td className="whitespace-nowrap px-1.5 py-1 font-semibold">{eur(l.montant)}</td>
                  <td className="max-w-[200px] px-1.5 py-1"><span className="line-clamp-2">{l.payeur ? <b>{l.payeur} · </b> : null}{l.libelle}</span></td>
                  <td className="px-1.5 py-1">
                    {l.candidats?.length > 1 ? (
                      <select value={k} disabled={!modifiable} onChange={(e) => setChoix((x) => ({ ...x, [l.idx]: Number(e.target.value) }))}
                        className="max-w-[220px] rounded border border-slate-300 px-1 py-0.5" data-testid={`klarys-releve-choix-${id}-${i}`}>
                        {l.candidats.map((o, j) => <option key={`${o.produit}-${o.ident}`} value={j}>{o.nom} · {o.produit_label} {o.reference}</option>)}
                      </select>
                    ) : cand ? <span>{cand.nom} · {cand.produit_label} {cand.reference}</span> : <span className="text-slate-400">—</span>}
                    {cand && <span className="block text-slate-500">{cand.echeances.length} échéance(s){Math.abs(cand.ecart) >= 0.01 ? ` · écart ${eur(cand.ecart)}` : ''}{cand.reactivation ? ' · réactivation' : ''}</span>}
                    {l.note && <span className="block text-amber-700">{l.note}</span>}
                  </td>
                  <td className="px-1.5 py-1"><span className={`rounded px-1 font-semibold ${cls}`} data-testid={`klarys-releve-confiance-${id}-${i}`}>{lab}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ApercuRecus = ({ c, id, exclus, setExclus, modifiable }) => {
  const dests = c.destinataires || [];
  const nb = dests.filter((d) => d.email_ok && !exclus.includes(d.cle)).length;
  const basculer = (k) => setExclus((x) => (x.includes(k) ? x.filter((v) => v !== k) : [...x, k]));
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-700" data-testid={`klarys-recus-${id}`}>
      <p className="flex items-center gap-1.5 font-semibold text-slate-800"><Receipt className="h-3.5 w-3.5 text-[#0a3d62]" />
        <span data-testid={`klarys-recus-compteur-${id}`}>{nb} / {dests.length} membre(s)</span>
        <span className="font-normal text-slate-500">— reçu de paiement + attestation à jour (PDF), classés dans la GED</span></p>
      <div className="max-h-48 overflow-y-auto rounded border border-slate-100">
        {dests.map((d, i) => (
          <label key={d.cle} className={`flex cursor-pointer items-center gap-2 border-b border-slate-100 px-2 py-1.5 last:border-0 ${d.email_ok && !exclus.includes(d.cle) ? '' : 'opacity-50'}`} data-testid={`klarys-recus-dest-${id}-${i}`}>
            <input type="checkbox" checked={d.email_ok && !exclus.includes(d.cle)} disabled={!modifiable || !d.email_ok} onChange={() => basculer(d.cle)}
              className="h-3.5 w-3.5 accent-[#0a3d62]" data-testid={`klarys-recus-check-${id}-${i}`} />
            <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{d.nom} · {d.produit_label} {d.reference}</span>
            <span className="text-slate-500">{eur(d.montant)}</span>
            <span className="truncate text-slate-500">{d.email_ok ? d.email : 'sans e-mail'}</span>
          </label>
        ))}
      </div>
      {c.apercu && (
        <div className="border-t border-slate-100 pt-2" data-testid={`klarys-recus-apercu-${id}`}>
          <p className="text-slate-500">Aperçu de l'e-mail pour {c.apercu.membre_nom}</p>
          <p className="mt-1">Objet : <b>{c.apercu.objet}</b></p>
          <p className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-slate-800">{c.apercu.message}</p>
        </div>
      )}
    </div>
  );
};

export const ApercuRecreer = ({ c, id, exclus, setExclus, modifiable }) => {
  const lignes = c.lignes || [];
  const nb = lignes.filter((l) => !exclus.includes(l.ident)).length;
  const basculer = (k) => setExclus((x) => (x.includes(k) ? x.filter((v) => v !== k) : [...x, k]));
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-rose-200 bg-white p-2.5 text-xs text-slate-700" data-testid={`klarys-recreer-${id}`}>
      <p className="flex items-center gap-1.5 font-semibold text-slate-800"><AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
        <span data-testid={`klarys-recreer-compteur-${id}`}>{nb} / {lignes.length} reçu(s) à recréer</span>
        <span className="font-normal text-slate-500">— ancienne version archivée, aucun e-mail</span></p>
      <div className="max-h-64 overflow-y-auto rounded border border-slate-100">
        {lignes.map((l, i) => (
          <label key={l.ident} className={`flex cursor-pointer items-start gap-2 border-b border-slate-100 px-2 py-1.5 last:border-0 ${exclus.includes(l.ident) ? 'opacity-50' : ''}`} data-testid={`klarys-recreer-ligne-${id}-${i}`}>
            <input type="checkbox" checked={!exclus.includes(l.ident)} disabled={!modifiable} onChange={() => basculer(l.ident)} className="mt-0.5 h-3.5 w-3.5 accent-[#0a3d62]" data-testid={`klarys-recreer-check-${id}-${i}`} />
            <span className="min-w-0 flex-1">
              <b>{l.nom}</b> · {l.produit_label} {l.reference}
              {l.recu?.numero && <span className="text-slate-500"> · {l.recu.numero}</span>}
              <span className="block text-rose-700">{(l.ecarts || []).join(' ; ')}</span>
              <span className="block text-slate-500">Souscription : global {eur(l.attendu?.global)} · réglé {eur(l.attendu?.paye)} · solde dû {eur(l.attendu?.reste)}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};
